import type { CSSProperties } from "react";
import type { DrinkListItem } from "../types/drink";

interface Props {
  drinks: DrinkListItem[];
  loading: boolean;
  onSelect: (slug: string) => void;
  emptyMessage: string;
}

export default function DrinkList({ drinks, loading, onSelect, emptyMessage }: Props) {
  if (loading) return <p style={styles.muted}>Loading…</p>;
  if (drinks.length === 0) return <p style={styles.muted}>{emptyMessage}</p>;

  return (
    <ul style={styles.list}>
      {drinks.map((d) => (
        <li key={d.id}>
          <button
            type="button"
            onClick={() => onSelect(d.slug)}
            style={styles.card}
          >
            <div style={styles.row}>
              <h3 style={styles.name}>{d.name}</h3>
              <ChipRow drink={d} />
            </div>
            <p style={styles.meta}>
              {d.method ?? "—"} · {d.glass ?? "—"} · v{d.version_number ?? "—"}
            </p>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Format an ISO timestamp into M-D-YYYY (e.g. "5-1-2026") for the chip.
 * Returns "" if the input is null/invalid so callers can fall back gracefully.
 */
function formatChipDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
}

function ChipRow({ drink }: { drink: DrinkListItem }) {
  const chips: { label: string; tone: "gold" | "blue" | "red" | "muted" }[] = [];
  if (drink.is_featured_today) chips.push({ label: "Featured", tone: "gold" });
  if (drink.is_recently_updated) {
    const dateStr = formatChipDate(drink.approved_at);
    chips.push({ label: dateStr ? `Updated ${dateStr}` : "Updated", tone: "blue" });
  }
  if (drink.service_status === "86d" || drink.has_unavailable_ingredient)
    chips.push({ label: "86", tone: "red" });
  if (chips.length === 0) chips.push({ label: drink.category, tone: "muted" });

  return (
    <div style={styles.chips}>
      {chips.map((c) => (
        <span key={c.label} style={{ ...styles.chip, ...chipTone[c.tone] }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

const chipTone: Record<string, CSSProperties> = {
  gold:  { background: "rgba(249,175,54,0.12)", color: "var(--gold)",   borderColor: "rgba(249,175,54,0.3)" },
  blue:  { background: "rgba(96,165,250,0.12)", color: "#93c5fd",       borderColor: "rgba(96,165,250,0.3)" },
  red:   { background: "rgba(224,80,64,0.16)",  color: "#FF8A7A",       borderColor: "rgba(224,80,64,0.4)"  },
  muted: { background: "var(--surface)",        color: "var(--muted)",  borderColor: "var(--border)"        },
};

const styles: Record<string, CSSProperties> = {
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 },
  card: {
    width: "100%",
    textAlign: "left",
    padding: 14,
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    cursor: "pointer",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  name: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.2,
    fontFamily: "Georgia, serif",
    color: "var(--text)",
  },
  meta: {
    margin: "6px 0 0",
    fontSize: 12,
    color: "var(--muted)",
    fontWeight: 600,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    flexShrink: 0,
    justifyContent: "flex-end",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 7px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: "16px 4px" },
};
